import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

/**
 * Per-device preferences, kept in localStorage (a convenience: losing them is harmless).
 * The shell applies `theme` and `motion` to <html> as data-theme and data-motion.
 */
export interface Prefs {
  theme: 'lilac' | 'sun';
  sound: boolean;
  haptics: boolean;
  /** 'system' follows the OS setting; 'reduce' and 'full' override it. */
  motion: 'system' | 'reduce' | 'full';
  set(patch: Partial<Omit<Prefs, 'set'>>): void;
}

const safeStorage = createJSONStorage(() => {
  try {
    const t = '__tts';
    localStorage.setItem(t, t);
    localStorage.removeItem(t);
    return localStorage;
  } catch {
    const mem = new Map<string, string>();
    return {
      getItem: (k: string) => mem.get(k) ?? null,
      setItem: (k: string, v: string) => void mem.set(k, v),
      removeItem: (k: string) => void mem.delete(k),
    };
  }
});

export const usePrefs = create<Prefs>()(
  persist(
    (set) => ({
      theme: 'lilac',
      sound: true,
      haptics: true,
      motion: 'system',
      set: (patch) => set(patch),
    }),
    { name: 'tts.prefs', storage: safeStorage, version: 1 },
  ),
);

/** Mirrors theme and motion prefs onto <html>. Returns the unsubscribe. */
export function applyPrefsToDocument(): () => void {
  const apply = (p: Prefs) => {
    const el = document.documentElement;
    if (p.theme === 'sun') el.dataset.theme = 'sun';
    else delete el.dataset.theme;
    if (p.motion === 'system') delete el.dataset.motion;
    else el.dataset.motion = p.motion;
    const meta = document.querySelector('meta[name="theme-color"]');
    meta?.setAttribute('content', p.theme === 'sun' ? '#ffffff' : '#cb94c2');
  };
  apply(usePrefs.getState());
  return usePrefs.subscribe(apply);
}

export function prefersReducedMotion(): boolean {
  const m = usePrefs.getState().motion;
  if (m !== 'system') return m === 'reduce';
  return typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
}
